import * as log from 'loglevel'
import { DirectUpload } from 'activestorage'
import TokenStorage from './TokenStorage'
import ErrorResponse from './ErrorResponse'
import AuthTokenExpiredError from './AuthTokenExpiredError'
import GuestReestablishRequiredError from './GuestReestablishRequiredError'
import { guestReestablishCode, isAuthTokenRejectedJsonApiError } from './authTokenErrors'
import { classifyFetchFailure, classifyHttpFailure } from './connectivityErrors'
import AnearUser from '../Models/AnearUser'

const qs = require('qs')

const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false, // might take longer on a device, but useful for Anear
  timeout: (process.env.REACT_APP_GEOCOORDS_TIMEOUT_SECS*1000), // Timeout getting coords
  maximumAge: (process.env.REACT_APP_CACHE_GEOCOORDS_SECONDS*1000) // cached coords duration
}

export default class ApiService {

  constructor() {
    this.tokenStorage = new TokenStorage()
    this.api_base_url = process.env.REACT_APP_ANEARAPP_API_URL
    this.direct_uploads_url = process.env.REACT_APP_ANEARAPP_DIRECT_UPLOADS_URL
    this.defaultHeaderObject = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Api-Key': process.env.REACT_APP_ANEARAPP_API_KEY
    }
    this.default_headers = new Headers(this.defaultHeaderObject)
  }

  getPosition = (options=GEOLOCATION_OPTIONS) => {
    // Check for fixed geolocation in development mode
    if (process.env.NODE_ENV === 'development') {
      // In dev mode, if REACT_APP_USER_FIXED_GEOLOCATION is not set, throw an error
      // to allow testing the geolocation error handling flow
      if (!process.env.REACT_APP_USER_FIXED_GEOLOCATION) {
        log.warn('REACT_APP_USER_FIXED_GEOLOCATION not set in development mode. Simulating geolocation error for testing.')
        return Promise.reject(new Error('Unable to obtain geolocation. Please try your event request again.'))
      }

      const fixedLocation = process.env.REACT_APP_USER_FIXED_GEOLOCATION.trim()
      const [lat, lng] = fixedLocation.split(',').map(coord => parseFloat(coord.trim()))

      if (!isNaN(lat) && !isNaN(lng)) {
        log.info(`Getting lat,lng of ${lat},${lng} from .env`)

        // Return a mock position object that matches the browser geolocation API structure
        const mockPosition = {
          coords: {
            latitude: lat,
            longitude: lng,
            accuracy: 0, // Fixed location has perfect accuracy
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null
          },
          timestamp: Date.now()
        }

        return Promise.resolve(mockPosition)
      } else {
        // If the env var is present but invalid format, also throw error in dev mode for testing
        log.warn(`REACT_APP_USER_FIXED_GEOLOCATION format invalid: "${fixedLocation}". Expected "lat,lng". Simulating geolocation error for testing.`)
        return Promise.reject(new Error('Unable to obtain geolocation. Please try your event request again.'))
      }
    }

    // Check if geolocation is available
    if (!navigator.geolocation) {
      return Promise.reject(new Error('Geolocation is not supported by this browser'))
    }

    // Retry logic: attempt up to 4 times (initial + 3 retries) with 250ms delay between attempts
    // This handles cases where geolocation may fail immediately after permission is granted
    const MAX_RETRIES = 3
    const RETRY_DELAY_MS = 250

    const attemptGetPosition = (attemptNumber) => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (attemptNumber > 1) {
              log.info(`Geolocation obtained successfully on attempt ${attemptNumber}`)
            }
            resolve(position)
          },
          (error) => {
            log.warn(`Geolocation attempt ${attemptNumber} failed:`, error.message)
            reject(error)
          },
          options
        )
      })
    }

    // Try initial attempt, then retry up to MAX_RETRIES times
    const attemptWithRetries = async (attemptNumber) => {
      try {
        return await attemptGetPosition(attemptNumber)
      } catch (error) {
        // If permission is denied, don't retry - user explicitly denied access
        if (error.code === error.PERMISSION_DENIED) {
          throw error
        }

        // If we've exhausted all retries, throw user-friendly error
        if (attemptNumber > MAX_RETRIES) {
          log.error(`Geolocation failed after ${MAX_RETRIES + 1} attempts`)
          throw new Error('Unable to obtain geolocation. Please try your event request again.')
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))

        // Retry
        return attemptWithRetries(attemptNumber + 1)
      }
    }

    return attemptWithRetries(1)
  }

  createAnearUser = json => {
    return new AnearUser(json.data, json.included)
  }

  setGeolocationHeader = async (request, required) => {
    // Only request geolocation if explicitly required
    // This ensures geolocation is never sent for events that don't require it
    if (!required) {
      return request
    }

    try {
      const position = await this.getPosition()
      request.headers.append('Geolocation', this.formatGeolocationHeader(position))
      return request
    } catch(error) {
      let msg = error.message
      // Handle geolocation API error codes
      if (error.code !== undefined) {
        switch(error.code) {
          case error.PERMISSION_DENIED:
            msg = "Geolocation is required to proceed"
            break
          case error.POSITION_UNAVAILABLE:
            msg = "Location information is unavailable"
            break
          case error.TIMEOUT:
            log.error("The request to get user location timed out")
            // For timeout, we still throw since geolocation is required
            msg = "Location request timed out"
            break
          case error.UNKNOWN_ERROR:
            break
          default:
            break
        }
      }
      // If geolocation is required and we can't get it, throw an error
      // This will prevent the API call from proceeding without geolocation
      throw new Error(msg)
    }
  }

  formatGeolocationHeader = ({coords, timestamp})=> {
    const posLatLng = `Position=[${coords.longitude},${coords.latitude}`
    const posAltitude = coords.altitude ? `,${coords.altitude}]` : ']'
    const accuracyTimestamp = `; Accuracy=${coords.accuracy}; Timestamp=${timestamp};`

    let geoOptional = ''
    if (coords.altitude && coords.altitudeAccuracy) geoOptional += ` AltitudeAccuracy=${coords.altitudeAccuracy};`
    if (coords.speed) geoOptional += ` Speed=${coords.speed};`
    if (coords.heading) geoOptional += ` Heading=${coords.heading};`

    return posLatLng + posAltitude + accuracyTimestamp + geoOptional
  }

  idParamUrlString = (resource, params) => {
    let idParam = ''
    if (params.id) {
      idParam = `/${params.id}`
      delete params.id
    }
    const paramString = qs.stringify(params)
    let queryParams = ''
    if (paramString) queryParams = `?${paramString}`
    return `${this.api_base_url}/${resource}${idParam}${queryParams}`
  }

  prepareGetRequest = (resource, params={}) => {
    const urlString = this.idParamUrlString(resource, params)
    return new Request(
      urlString, {
        method: 'GET',
        headers: this.default_headers
      }
    )
  }

  get = async (resource, params, needsLocation) => {
    const request = this.prepareGetRequest(resource, params)
    return this.issueRequest(request, needsLocation)
  }

  messagingAuth = async () => {
    return this.get('messaging_auth', {}, false)
  }

  post = async (resource, attributes, needsLocation, relationships={}) => {
    const payload = this.formatPayload(resource, attributes, relationships)
    const request = new Request(
      `${this.api_base_url}/${resource}`, {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, needsLocation)
  }

  postAction = async (resource, needsLocation = false, keepalive = false) => {
    const request = new Request(
      `${this.api_base_url}/${resource}`, {
        method: 'POST',
        headers: this.default_headers,
        keepalive
      }
    )
    return this.issueRequest(request, needsLocation)
  }

  put = async (resource, id, attributes, needsLocation, relationships={}) => {
    const payload = this.formatPayload(resource, attributes, relationships)
    const urlString = `${this.api_base_url}/${resource}/${id}`
    const request = new Request(
      urlString, {
        method: 'PUT',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, needsLocation)
  }

  httpDelete = async (resource, id=null) => {
    const idStr = (id ? `/${id}` : '')
    const urlString = `${this.api_base_url}/${resource}${idStr}`
    const request = new Request(
      urlString, {
        method: 'DELETE',
        headers: this.default_headers
      }
    )
    return this.issueRequest(request, false)
  }

  setAuthorizationHeader = request => {
    if (this.tokenStorage.hasAuthMeta()) {
      this.updateAuthHeader(request)
    }
  }

  getAuthorizationBearer = () => {
    const authMeta = this.tokenStorage.getAuthMeta()
    return `Bearer ${authMeta['auth-token']}`
  }

  updateAuthHeader = (request) => {
    request.headers.append('Authorization', this.getAuthorizationBearer())
  }

  /**
   * fetch wrapper: no-response / browser network failures become
   * NetworkConnectivityError with venue-friendly copy.
   */
  fetchWithConnectivity = async (request, { context = 'api' } = {}) => {
    try {
      return await fetch(request)
    } catch (err) {
      const classified = classifyFetchFailure(err, { context })
      throw classified || err
    }
  }

  getGuestAccount = async () => {
    const request = this.prepareGetRequest("accounts")
    const response = await this.fetchWithConnectivity(request)
    const json = await this.checkStatus(response)
    this.setAuthMetaFromResponse(json)
    return this.createAnearUser(json)
  }

  issueRequest = async (request, needsLocation) => {
    this.setAuthorizationHeader(request)
    request = await this.setGeolocationHeader(request, needsLocation)
    const response = await this.fetchWithConnectivity(request)
    const json = await this.checkStatus(response)
    this.setAuthMetaFromResponse(json)
    return json
  }

  formatPayload = (resource, attributes, relationships) => {
    return {
      data: {
        type: resource,
        attributes,
        relationships: this.formatRelationships(relationships)
      }
    }
  }

  formatRelationships = (relationships) => {
    //
    // e.g. formatRelationships({event: eventId, user: userId})
    //
    const output = {}

    Object.entries(relationships).forEach(rel => {
      const resource = rel[0]
      const id = rel[1]

      output[resource] = {
        data: {
          type: resource + 's',
          id: id
        }
      }
    })
    return output
  }

  checkStatus = async response => {
    if (response.status === 204) {
      return {}
    }

    // Gateway / timeout statuses → universal connectivity copy (not raw HTTP text).
    const connectivity = classifyHttpFailure(response.status)
    if (connectivity) {
      throw connectivity
    }

    const responseClone = response.clone()
    let json = null
    try {
      json = await response.json()
    } catch (_parseError) {
      const text = await responseClone.text()
      throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
    }

    if (response.ok) {
      return json
    } else {
      if (!json || !json.errors || !json.errors[0]) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const error = json.errors[0]

      //
      // An expired guest is still the same bearer. Callers must keep it and
      // open the welcome screen. A registered rejection still clears.
      //
      if (error.code === guestReestablishCode) {
        throw new GuestReestablishRequiredError(
          error.detail || error.title,
          json.meta
        )
      }

      //
      // Expired or otherwise-invalid auth manifests as JSONAPI status 401,
      // title "Not Authenticated", or invalid-token detail text — often with
      // outer HTTP 400 (ApiErrorSerializer default). Normalize all of these
      // so callers can clear stale localStorage and recover gracefully.
      //
      if (isAuthTokenRejectedJsonApiError(error)) {
        throw new AuthTokenExpiredError(error.detail || error.title)
      }

      throw new ErrorResponse(json)
    }
  }

  setAuthMetaFromResponse = json => {
    const meta = json?.meta
    // Merchant list endpoints (e.g. discover_apps) return pagination meta only.
    // Never replace the stored session when the response lacks auth-token.
    if (!meta?.['auth-token']) return

    this.tokenStorage.setAuthMeta(meta)
  }

  createUpload = (upload) => {
    return new Promise((resolve, reject) => {
      upload.create((err, blob) => {
        if (err) {
          const classified = classifyFetchFailure(err, { context: 'upload' })
          reject(classified || err)
          return
        }
        resolve(blob)
      })
    })
  }

  activeStorageUpload = async (userName, blob) => {
    // DirectUpload needs a named File. Do not mutate the incoming Blob/File:
    // File.name and File.lastModifiedDate are getter-only (profile selfie resize
    // now hands us a File from prepareEventImageForUpload).
    const file = new File([blob], `${userName}_avatar.jpg`, {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now()
    })

    const upload = new DirectUpload(
      file,
      this.direct_uploads_url,
      {
        directUploadWillCreateBlobWithXHR: (xhr) => {
          // This will decorate the requests with the access token header so you won't get a 401
          xhr.setRequestHeader('Authorization', this.getAuthorizationBearer())
        }
      }
    )

    log.info(`uploading ${file.name}...`)

    try {
      return await this.createUpload(upload)
    } catch (err) {
      const classified = classifyFetchFailure(err, { context: 'upload' })
      throw classified || err
    }
  }
}

